import { ActionContext } from "vuex";
import { isEmpty, sortBy } from "lodash";
import { LoggerService as lgg } from "vue-components-library";

import router from "@/App/router";
import { remove } from "@/Utils/arrayUtils";
import { ITag } from "@/Case/models/ITag";
import { TwiageCase } from "@/Case/models/TwiageCase";
import { IMedia } from "../models/IMedia";
import CaseService from "@/Case/services/Case.Service";
import { addExclusiveTag, updateTagsSelectionState } from "@/Case/store/tags.action.helpers";
import { TeamSelectionService } from "@/TeamSelection/services/TeamSelection.Service";
import MonitorService from "@/Case/services/Monitor.Service";
import { format, parseISO } from "date-fns";
import { Storage as storage } from "@/App/services/LocalStorage";
import { getCasePlaceholderId } from "@/Case/store/getCasePlaceholderId";
import { casePlaceholderPrefix } from "@/Case/store/casePlaceholderPrefix";
import { CaseUpdateEventType } from "@/Case/CaseEvent";

const getDefaultCase = (placeholderId = getCasePlaceholderId()): TwiageCase => ({
  id: placeholderId,
  caseNumber: null,
  destinationHospital: { id: null },
  emsAgency: null,
  complaint: { id: null, sections: [] },
  messages: [],
  uploads: [],
  tags: [],
  truck: null,
  gender: null,
  monitorIntegration: null,
  patientInfo: null,
  ownedBy: null,
  createdAt: null,
});

const LOG_TAG = "[CASE STORE]";

function getCaseJson() {
  return storage.getItem("case");
}

function getTransferredComplaints() {
  return storage.getItem("transferredComplaints");
}

function getDefaultState() {
  return {
    caseActive: storage.getItem("caseActive") === "true" || false,
    case: getCaseJson() ? JSON.parse(getCaseJson() || "") : getDefaultCase(),
    transferredComplaints: getTransferredComplaints() ? JSON.parse(getTransferredComplaints() || "") : [],
    location: {},
    getLocation: 0,
    locationChanged: 0,
    tab: "complaints",
    liveConsult: {
      started: false,
      minimized: false,
    },
    initialLocation: {},
    locationError: false,
  };
}

function getLocation() {
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { latitude, longitude } = pos.coords;
      state.location = { latitude, longitude };
      state.locationChanged = state.locationChanged + 1;
      CaseService.locationUpdate(state.case.id, {
        latitude: latitude,
        longitude: longitude,
      });
      state.locationError = false;
      if (isEmpty(state.initialLocation)) {
        state.initialLocation = { latitude, longitude };
        lgg.info(`${LOG_TAG} initialLocation updated`, {
          case: state.case.id,
          latitude,
          longitude,
          at: new Date().toISOString(),
        });
      }
    },
    (err) => {
      if (!state.locationError) {
        state.locationError = true;
        lgg.error(`${LOG_TAG} Failed to update geolocation position: ${err.message}`, {
          case: { id: state.case.id, at: new Date().toISOString() },
        });
      }
    },
    {
      enableHighAccuracy: true,
      maximumAge: 0,
    },
  );
}

function playSound() {
  const audio = new Audio("/telegraph.mp3");
  audio.play();
}

const state = getDefaultState();

const getters = {
  isCaseActive: (state: any) => state.caseActive,
  twiageCase: (state: any) => state.case,
  destinationHospital: (state: any) => state.case?.destinationHospital,
  caseId: (state: any, getters: any) => getters.twiageCase.id,
  messages: (state: any) => state.case.messages,
  uploads: (state: any) => state.case.uploads,
  location: (state: any) => state.case.location,
  locationChanged: (state: any) => state.case.locationChanged,
  selectedTags: (state: any) => state.case.tags || [],
  mediaCount:
    (state: any) =>
    (type: string): Number => {
      if (state.case && state.case.uploads) {
        return (state.case.uploads.filter((f: IMedia) => f.type === type) || []).length;
      }
      return 0;
    },
  tab: (state: any) => {
    return state.tab;
  },
  liveConsult: (state: any) => state.liveConsult,
  complaints: (state: any, getters: any, rootState: any, rootGetters: any) => {
    const teamComplaints = rootGetters["TeamSelection/complaints"];
    const transferredComplaints = state.transferredComplaints;
    if (transferredComplaints.length) {
      return transferredComplaints;
    }
    return teamComplaints;
  },
  linkedMonitor: (state: any, getters: any, rootState: any, rootGetters: any): void | object => {
    if (!state.case || !state.case.monitorIntegration) {
      return;
    }
    const { monitorIntegration } = state.case;
    const selectedTeam = rootGetters["TeamSelection/selectedTeam"];

    if (!selectedTeam.monitors || !selectedTeam.monitors.length) {
      return;
    }

    const linkedMonitor = selectedTeam.monitors.find((monitor) => monitor._id === monitorIntegration.monitorId);
    if (!linkedMonitor) {
      return;
    }
    return { ...linkedMonitor, ...state.case.monitorIntegration };
  },
  patientName(state: any): string {
    if (!state.case?.patientInfo) {
      return "";
    }
    const { patientInfo } = state.case;
    const firstName = patientInfo.find(({ key }) => key === "firstName");
    const lastName = patientInfo.find(({ key }) => key === "lastName");
    const getValueFrom = (field) => field?.value ?? "";

    return `${getValueFrom(firstName)} ${getValueFrom(lastName)}`.trim();
  },
  patientDateOfBirth(state: any): string {
    if (!state.case?.patientInfo) {
      return "";
    }
    const { patientInfo } = state.case;
    const dateOfBirthField = patientInfo.find(({ key }) => key === "dateOfBirth");
    let dateOfBirthValue = dateOfBirthField?.value ?? "";
    if (dateOfBirthValue) {
      dateOfBirthValue = format(parseISO(dateOfBirthValue), "MM/dd/yyyy");
    }

    return `${dateOfBirthValue}`.trim();
  },
  hasPatientInfo(state: any, getters: any) {
    return !!(getters.patientName || getters.patientDateOfBirth);
  },
  missingRequiredPatientFields(state: any, getters: any): string[] {
    const { hospitalRequiresPatientNameAndDOB } = require("@/CaseFeedback/store/featureResolver");
    if (!hospitalRequiresPatientNameAndDOB(getters.destinationHospital)) {
      return [];
    }

    const patientInfo = state.case?.patientInfo || [];
    const requiredFields = [
      { key: "firstName", displayName: "First Name" },
      { key: "lastName", displayName: "Last Name" },
      { key: "dateOfBirth", displayName: "DOB" },
    ];

    const isFieldMissing = (fieldKey: string): boolean => {
      const field = patientInfo.find(({ key }) => key === fieldKey);
      return !field?.value || field.value.trim() === "";
    };

    return requiredFields.filter(({ key }) => isFieldMissing(key)).map(({ displayName }) => displayName);
  },
};

export const actions = {
  setCase(context: ActionContext<any, any>, twiageCase: any) {
    context.commit("SET_CASE", twiageCase);
  },
  async startCase(context: ActionContext<any, any>) {
    const placeholderId = getCasePlaceholderId();

    try {
      context.commit("RESET_STATE");
      const teamId = context.rootState.TeamSelection.selectedTeam.id;
      const truckId = context.rootState.TruckSelection.selectedTruck.id;
      context.commit("START_CASE", { teamId, truckId, placeholderId });
      const newCase = await CaseService.startCase(
        {
          emsAgency: teamId,
          truck: truckId,
          beganAt: Date.now(),
        },
        { placeholderId },
      );

      const currentCaseId = newCase.id || placeholderId;

      context.commit("MODIFY_CASE", {
        eventType: CaseUpdateEventType.caseStarted,
        data: {
          id: currentCaseId,
          caseNumber: newCase.caseNumber,
          channelId: newCase.channelId,
          ownedBy: newCase.ownedBy,
          createdAt: newCase.createdAt,
        },
      });

      lgg.info(`${LOG_TAG} Case started`, {
        id: currentCaseId,
        createdAt: newCase.createdAt,
        emsAgency: context.rootState.TeamSelection.selectedTeam.name,
        ownedBy: newCase.ownedBy,
        user: context.rootState.Login.user.displayName,
        at: new Date().toISOString(),
      });
      context.dispatch("subscribeToPushes", currentCaseId);
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to start Case`, { error });
      // throw error;
    }
    if (window["electron"] && context.rootState.GpsSource.config.source === "port") {
      context.dispatch("GpsSource/startListening", null, { root: true });
    }
  },
  async subscribeToPushes(context: ActionContext<any, any>, twiageCaseId: string) {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      if (permission === "granted" && !twiageCaseId.includes(casePlaceholderPrefix)) {
        context.dispatch("PushNotification/subscribe", `ems-${twiageCaseId}`, { root: true });
      }
    }
  },

  trackLocation(context: ActionContext<any, any>) {
    if (window["electron"] && context.rootState.GpsSource.config.source === "port") {
      lgg.info(`${LOG_TAG} GpsStore config`, context.rootState.GpsSource.config);
      state.getLocation = <any>setInterval(() => {
        state.locationChanged = state.locationChanged + 1;
        CaseService.locationUpdate(state.case.id, context.rootState.GpsSource.location);
        state.locationError = false;
        if (isEmpty(state.initialLocation)) {
          state.initialLocation = context.rootState.GpsSource.location;
          lgg.info(`${LOG_TAG} Electron initialLocation updated`, {
            case: state.case.id,
            location: context.rootState.GpsSource.location,
            at: new Date().toISOString(),
          });
        }
      }, 15 * 1000);
    } else {
      if (!("geolocation" in navigator)) {
        lgg.info(`${LOG_TAG} Geolocation is unavailable`);
        return;
      }
      state.getLocation = <any>setInterval(getLocation, 15 * 1000);
    }
  },

  modifyCase(context: ActionContext<any, any>, event: { eventType: CaseUpdateEventType }) {
    switch (event.eventType) {
      case CaseUpdateEventType.complaint:
        context.dispatch("complaintEvent", event);
        break;
      case CaseUpdateEventType.tag:
        context.dispatch("tagClickedEvent", event);
        break;
      case CaseUpdateEventType.dataEnabledTag:
        context.dispatch("dataEnabledTagClickedEvent", event);
        break;
      case CaseUpdateEventType.age:
        context.dispatch("ageEvent", event);
        break;
      case CaseUpdateEventType.gender:
        context.dispatch("genderEvent", event);
        break;
      case CaseUpdateEventType.chat:
        context.dispatch("chatEvent", event);
        break;
      case CaseUpdateEventType.chatMessage:
        context.dispatch("chatMessageEvent", event);
        break;
      case CaseUpdateEventType.hospital:
        context.dispatch("hospitalEvent", event);
        break;
      case CaseUpdateEventType.photo:
      case CaseUpdateEventType.id:
      case CaseUpdateEventType.ekg:
      case CaseUpdateEventType.video:
      case CaseUpdateEventType.voiceMemo:
        context.dispatch("mediaEvent", event);
        break;
      case CaseUpdateEventType.liveConsult:
        context.dispatch("liveConsultEvent", event);
        break;
      case CaseUpdateEventType.monitor:
        context.dispatch("monitorEvent", event);
        break;
      case CaseUpdateEventType.patientInfo:
        context.dispatch("patientInfoEvent", event);
        break;
      default:
    }
  },
  async chatEvent(context: ActionContext<any, any>, event: any) {
    const { state } = context;
    try {
      context.commit("CHAT_EVENT", event);
      await CaseService.sendChat(state.case.id, {
        clientCreatedAt: event.data.clientCreatedAt,
        deviceId: "web_ems",
        message: event.data.content,
        timestamp: event.data.timestamp,
        source: "web_ems",
        senderOrganization: state.case.emsAgency,
      });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to send chat message`, {
        error,
        case: { id: state.case.id },
      });
      throw error;
    }
  },
  chatMessageEvent(context: ActionContext<any, any>, event: any) {
    if (context.state.case && context.state.case.messages) {
      if (!context.state.case.messages.find((m) => m.timestamp === event.data.timestamp)) {
        if (event.data.source !== "web_ems") {
          playSound();
        }
        context.commit("CHAT_EVENT", event);
      }
    }
  },
  async complaintEvent(context: ActionContext<any, any>, event: any) {
    try {
      if (!event.data._id) {
        event.data._id = event.data.id;
      }
      if (!event.data || event.data.id === context.state.case.complaint.id) {
        return;
      }
      context.commit("MODIFY_CASE", event);
      await CaseService.modifyCase(context.state.case.id, {
        complaint: event.data,
      });
      lgg.info(`${LOG_TAG} Complaint changed`, {
        caseId: context.state.case.id,
        complaint: event.data.name,
        at: new Date().toISOString(),
      });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to change complaint`, {
        case: { id: context.state.case.id },
      });
      throw error;
    }
  },
  tagClickedEvent(context: ActionContext<any, any>, event: any) {
    if (!event.data || !event.data.tag) {
      return;
    }

    const isTagSelected = context.getters.selectedTags.some(
      (selectedTag: ITag) => selectedTag.id === event.data.tag.id,
    );

    if (isTagSelected) {
      return updateTagsSelectionState(context, event, event.data.tag, false);
    } else {
      const mutuallyExclusiveTag = remove(
        context.getters.selectedTags,
        (tag: ITag) => tag.groupIdentifier === event.data.tag.groupIdentifier,
      );
      if (mutuallyExclusiveTag) {
        return addExclusiveTag(context, event, event.data.tag, mutuallyExclusiveTag);
      } else {
        return updateTagsSelectionState(context, event, event.data.tag, true);
      }
    }
  },
  dataEnabledTagClickedEvent(context: ActionContext<any, any>, event: any) {
    if (!event.data || !event.data.tag) {
      return;
    }

    return updateTagsSelectionState(context, event, event.data.tag, true, event.data.fields);
  },
  async hospitalEvent(context: ActionContext<any, any>, event: any) {
    try {
      if (!event.data || event.data.id === context.state.case.destinationHospital.id) {
        return;
      }
      context.commit("MODIFY_CASE", event);
      await CaseService.modifyCase(context.state.case.id, {
        destinationHospital: event.data,
      });
      lgg.info(`${LOG_TAG} Destination hospital changed`, {
        caseId: context.state.case.id,
        destinationHospital: event.data.name,
        at: new Date().toISOString(),
      });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to change destination hospital`, {
        error,
        destinationHospital: event.data,
      });
      throw error;
    }
  },
  async genderEvent(context: ActionContext<any, any>, event: any) {
    try {
      context.commit("MODIFY_CASE", event);
      await CaseService.modifyCase(context.state.case.id, {
        gender: event.data,
      });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to change gender`, {
        error,
        case: { id: context.state.case.id },
        gender: event.data,
      });
      throw error;
    }
  },
  async ageEvent(context: ActionContext<any, any>, event: any) {
    try {
      if (!event.data) {
        return;
      }
      context.commit("MODIFY_CASE", event);
      await CaseService.modifyCase(context.state.case.id, {
        age: event.data.age,
        agePeriod: event.data.agePeriod,
      });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to change age`, { error });
      throw error;
    }
  },
  mediaEvent(context: ActionContext<any, any>, event: any) {
    context.commit("MEDIA_EVENT", {
      id: event.id,
      type: event.eventType,
    });

    if (navigator.onLine && context.state.case.id) {
      context.dispatch("uploadMedia", event);
    } else {
      context.dispatch("delayMediaUpload", event);
    }
  },
  delayMediaUpload(context: ActionContext<any, any>, event: any) {
    setTimeout(() => {
      if (navigator.onLine && context.state.case.id && !context.state.case.id.includes(casePlaceholderPrefix)) {
        context.dispatch("uploadMedia", event);
      } else {
        context.dispatch("delayMediaUpload", event);
      }
    }, 2000);
    lgg.info(`${LOG_TAG} Media upload was delayed. Waiting for internet connection.`, {
      case: { id: context.state.case.id },
    });
  },
  async uploadMedia(context: ActionContext<any, any>, event: any) {
    try {
      const formData = new FormData();
      formData.append("type", event.eventType);
      formData.append("data", event.data, event.id);

      await CaseService.mediaUpdate(context.state.case.id, formData);
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to upload media`, {
        error,
        case: { id: context.state.case.id },
      });
      throw error;
    }
  },
  async loadChatOffline(context: ActionContext<any, any>, lastOnline: number) {
    if (!context.state.caseActive || context.state.case.id.includes(casePlaceholderPrefix)) {
      return;
    }
    context.dispatch(
      "PubNubSubscription/subscribeChatChannel",
      {
        channelId: context.state.case.channelId,
        uuid: context.state.case.ownedBy,
      },
      { root: true },
    );
    try {
      const { messages } = await CaseService.getChatHistory(context.state.case.id);
      const res = messages.filter((message) => message.timestamp > lastOnline);
      if (res?.length > 0) {
        context.dispatch("PubNubSubscription/addUnreadChatMessage", res.length, { root: true });
        context.commit("CHAT_OFFLINE_HISTORY", messages);
        lgg.info(`${LOG_TAG} Loaded Chat offline history`, {
          case: { id: context.state.case.id },
        });
      }
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to load Chat offline history`, {
        error,
        case: { id: context.state.case.id },
      });
      throw error;
    }
  },
  setTab(context: ActionContext<any, any>, tab: string) {
    context.commit("SET_TAB", tab);
  },
  async getLiveConsultRoom(context: ActionContext<any, any>) {
    try {
      const room = await CaseService.getLiveConsultRoom(context.state.case.id);
      lgg.info(`${LOG_TAG} Fetched Live Consult room`, {
        case: { id: context.state.case.id },
      });
      return room;
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to fetch Live Consult room`, {
        error,
        case: { id: context.state.case.id },
      });
      throw error;
    }
  },
  liveConsultEvent(context: ActionContext<any, any>, event: any) {
    context.commit("SET_LIVE_CONSULT", event.data.liveConsult);
  },
  async closeCase(context: ActionContext<any, any>) {
    const caseId = context.state.case.id;
    try {
      context.dispatch("PushNotification/unsubscribe", `ems-${caseId}`, {
        root: true,
      });
      context.dispatch("Voip/endCall", undefined, { root: true });
      await context.dispatch("CaseFeedback/setPendingFeedback", caseId, { root: true });
      context.commit("CLOSE_CASE");
      context.commit("RESET_STATE");
      router.push({ name: "start" });

      await CaseService.modifyCase(caseId, { arrivedAt: Date.now() });
      lgg.info(`${LOG_TAG} Case closed`, {
        caseId: caseId,
        at: new Date().toISOString(),
      });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to close Case`, {
        error,
        case: { id: caseId },
      });
      throw error;
    }
  },
  clearCase(context: ActionContext<any, any>) {
    const caseId = context.state.case.id;

    context.dispatch("PushNotification/unsubscribe", `ems-${caseId}`, {
      root: true,
    });
    context.dispatch(
      "PubNubSubscription/unsubscribeChatChannel",
      {
        channelId: context.state.case.channelId,
      },
      {
        root: true,
      },
    );

    context.commit("CLOSE_CASE");
    context.commit("RESET_STATE");

    router.push({ name: "start" });
  },
  resetState(context: ActionContext<any, any>) {
    context.commit("RESET_STATE");
  },
  async loadTransferredComplaints(context: ActionContext<any, any>) {
    const selectedComplaintSet = context.getters.twiageCase.complaintSet;
    const teamComplaintSet = context.rootGetters["TeamSelection/complaintSet"];
    const caseHasTeamsComplaintSet = selectedComplaintSet === teamComplaintSet;
    if (!selectedComplaintSet || caseHasTeamsComplaintSet) {
      return;
    }
    try {
      const complaintSet = await TeamSelectionService.getComplaintSet(selectedComplaintSet);
      context.commit("SET_TRANSFER_COMPLAINTS", complaintSet.complaints);
      lgg.info(`${LOG_TAG} Loaded transferred complaints`);
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to load transferred complaints`, {
        error,
      });
      throw error;
    }
  },
  async monitorEvent(context: ActionContext<any, any>, event): Promise<void> {
    const {
      twiageCase: { id: caseId, caseNumber },
    } = context.getters;

    try {
      context.commit("MODIFY_CASE", event);
      const { monitorIntegration }: any = await MonitorService.linkToCase({ caseId, ...event.data });
      context.commit("MODIFY_CASE", { ...event, data: monitorIntegration });
      lgg.info(`${LOG_TAG} Monitor linked to Case`, { caseId, caseNumber, event });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to link Monitor to Case`, { caseId, caseNumber, event, error });
      throw error;
    }
  },
  async patientInfoEvent(context: ActionContext<any, any>, event): Promise<void> {
    const {
      twiageCase: { id: caseId, caseNumber },
    } = context.getters;
    const {
      data: { formId, fields },
    } = event;

    try {
      context.commit("MODIFY_CASE", event);
      const response = await CaseService.savePatientInfo(caseId, formId, { fields });
      lgg.info(`${LOG_TAG} Patient Info saved.`, { caseId, caseNumber, event, response });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to save Patient Info.`, { caseId, caseNumber, event, error });
    }
  },
};

const mutations = {
  SET_CASE(state: any, twiageCase: any) {
    state.caseActive = true;
    state.case = getDefaultCase();
    state.case = Object.assign(state.case, twiageCase);

    storage.setItem("caseActive", JSON.stringify(state.caseActive));
    storage.setItem("case", JSON.stringify(state.case));
  },
  SET_TRANSFER_COMPLAINTS(state: any, complaints: any) {
    if (complaints && complaints.length) {
      state.transferredComplaints = complaints;
    }
  },
  START_CASE(
    state: any,
    { teamId, truckId, placeholderId }: { teamId: string; truckId: string; placeholderId: string },
  ) {
    state.caseActive = true;
    state.case = getDefaultCase(placeholderId);
    state.case.emsAgency = { _id: teamId };
    state.case.truck = { _id: truckId };
    state.case.caseNumber = "...";
    storage.removeItem("caseTransfer");
    storage.removeItem("caseTransferType");
    storage.removeItem("caseTransferCase");
    storage.removeItem("transferredComplaints");
    storage.setItem("caseActive", JSON.stringify(state.caseActive));
    storage.setItem("case", JSON.stringify(state.case));
  },
  MODIFY_CASE(state: any, event: any) {
    const { eventType, data } = event;
    switch (eventType) {
      case CaseUpdateEventType.caseStarted: {
        const { caseNumber, id, channelId, ownedBy, createdAt } = data;
        state.case.id = id;
        state.case.caseNumber = caseNumber;
        state.case.channelId = channelId;
        state.case.ownedBy = ownedBy;
        state.case.createdAt = createdAt;
        break;
      }
      case CaseUpdateEventType.complaint:
        state.case.complaint = data;
        break;
      case CaseUpdateEventType.tag:
        if (!state.case.tags) {
          state.case.tags = [];
        }
        if (data.selected) {
          state.case.tags.push(data.tag);
        } else {
          remove(state.case.tags, (tag: ITag) => tag.id === data.tag.id);
        }
        break;
      case CaseUpdateEventType.dataEnabledTag: {
        if (!state.case.tags) {
          state.case.tags = [];
        }

        const dataEnabledTag = state.case.tags.find((tag: ITag) => tag.id === data.tag.id);
        const updatedAt = new Date().toISOString();
        const value = { fields: data.fields, updatedAt };

        if (dataEnabledTag === undefined) {
          data.tag.values = [value];
          state.case.tags.unshift(data.tag);
        } else {
          dataEnabledTag.values.unshift(value);
        }

        break;
      }
      case CaseUpdateEventType.age:
        state.case.age = data.age;
        state.case.agePeriod = data.agePeriod;
        break;
      case CaseUpdateEventType.gender:
        state.case.gender = data;
        break;
      case CaseUpdateEventType.hospital:
        state.case.destinationHospital = data;
        break;
      case CaseUpdateEventType.monitor:
        state.case.monitorIntegration = data;
        break;
      case CaseUpdateEventType.patientInfo:
        state.case.patientInfo = data.fields;
        break;
    }
    storage.setItem("case", JSON.stringify(state.case));
  },
  CHAT_EVENT(state: any, event: any) {
    event.data.id = new Date().toISOString();
    state.case.messages.push(event.data);
    storage.setItem("case", JSON.stringify(state.case));
  },
  CHAT_OFFLINE_HISTORY(state: any, messages: Array<any>) {
    const newMessages: any[] = [];
    messages.forEach((msg: any) => {
      let addMsg = true;
      state.case.messages.forEach((caseMsg: any) => {
        if (caseMsg.timestamp === msg.timestamp) {
          addMsg = false;
        }
      });
      if (addMsg) {
        newMessages.push(msg);
      }
    });
    state.case.messages = sortBy(state.case.messages.concat(newMessages), ["timestamp"]);
    storage.setItem("case", JSON.stringify(state.case));
  },
  MEDIA_EVENT(state: any, event: any) {
    state.case.uploads.push(event);
    storage.setItem("case", JSON.stringify(state.case));
  },
  UPDATE_CASE_FROM_SERVER(state: any, data: any) {
    state.case = Object.assign(state.case, data);
    storage.setItem("case", JSON.stringify(state.case));
  },
  CLOSE_CASE(state: any) {
    state.caseActive = false;
    clearInterval(state.getLocation);
    state.messages = [];
    state.case = {};
    state.location = {};
    state.getLocation = 0;
    storage.setItem("caseActive", JSON.stringify(state.caseActive));
    storage.removeItem("case");
    storage.removeItem("caseTransfer");
    storage.removeItem("caseTransferType");
    storage.removeItem("caseTransferCase");
    storage.removeItem("transferredComplaints");
  },
  SET_TAB(state: any, tab: string) {
    state.tab = tab;
  },
  RESET_STATE(state: any): void {
    const initialState: any = getDefaultState();
    Object.keys(initialState).forEach((key) => {
      state[key] = initialState[key];
    });
  },
  SET_LIVE_CONSULT(state: any, liveConsult: any) {
    state.liveConsult = liveConsult;
  },
};

const store: any = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default store;
